import type { AvailabilityLevel, Dilution, Grade, ZoneNumber } from '../model/types';

/** One cell of Table D.1. */
export type ZoneOutcome =
  /** "Non-hazardous (Zone n NE)": a theoretical zone of negligible extent. */
  | { kind: 'non-hazardous'; negligible: ZoneNumber }
  /** "Zone n", optionally "(Zone m NE)" inside it. */
  | { kind: 'zone'; zone: ZoneNumber; negligible?: ZoneNumber }
  /** "Zone a + Zone b": a surrounded by b. */
  | { kind: 'nested'; inner: ZoneNumber; outer: ZoneNumber; note?: 'c' }
  /** "Zone 1 or Zone 0", "Zone 1 and even Zone 0": the engineer decides (notes c, d). */
  | { kind: 'judgement'; options: ZoneNumber[]; note: 'c' | 'd' };

const NH = (n: ZoneNumber): ZoneOutcome => ({ kind: 'non-hazardous', negligible: n });
const Z = (zone: ZoneNumber, negligible?: ZoneNumber): ZoneOutcome =>
  negligible === undefined ? { kind: 'zone', zone } : { kind: 'zone', zone, negligible };
const N = (inner: ZoneNumber, outer: ZoneNumber, note?: 'c'): ZoneOutcome =>
  note ? { kind: 'nested', inner, outer, note } : { kind: 'nested', inner, outer };
const J = (note: 'c' | 'd'): ZoneOutcome => ({ kind: 'judgement', options: [1, 0], note });

type Row = { high: Record<AvailabilityLevel, ZoneOutcome>; medium: Record<AvailabilityLevel, ZoneOutcome>; low: ZoneOutcome };

/** Table D.1 — zones for grade of release and effectiveness of ventilation. */
export const TABLE_D1: Record<Grade, Row> = {
  continuous: {
    high: { good: NH(0), fair: Z(2, 0), poor: Z(1, 0) },
    medium: { good: Z(0), fair: N(0, 2, 'c'), poor: N(0, 1) },
    low: Z(0),
  },
  primary: {
    high: { good: NH(1), fair: Z(2, 1), poor: Z(2, 1) },
    medium: { good: Z(1), fair: N(1, 2), poor: N(1, 2) },
    low: J('c'),
  },
  secondary: {
    high: { good: NH(2), fair: NH(2), poor: Z(2) },
    medium: { good: Z(2), fair: Z(2), poor: Z(2) },
    low: J('d'),
  },
};

export function zoneOutcome(grade: Grade, dilution: Dilution, availability: AvailabilityLevel): ZoneOutcome {
  const row = TABLE_D1[grade];
  return dilution === 'low' ? row.low : row[dilution][availability];
}

export const NOTE_TEXT: Record<'c' | 'd', string> = {
  c: 'A small inner zone where the release is not controlled by the ventilation, and a larger outer zone for when ventilation fails (Table D.1, note c).',
  d: 'Zone 0 if the ventilation is so weak and the release such that an explosive atmosphere exists virtually continuously (Table D.1, note d).',
};

export function describeOutcome(o: ZoneOutcome): string {
  switch (o.kind) {
    case 'non-hazardous':
      return `Non-hazardous (Zone ${o.negligible} NE)`;
    case 'zone':
      return o.negligible === undefined ? `Zone ${o.zone}` : `Zone ${o.zone} (Zone ${o.negligible} NE)`;
    case 'nested':
      return `Zone ${o.inner} + Zone ${o.outer}`;
    case 'judgement':
      return o.note === 'd' ? 'Zone 1 and even Zone 0' : 'Zone 1 or Zone 0';
  }
}
